import * as d3 from "d3"
import * as React from "react"
import Slider from "@mui/material/Slider"
import { actions, AppState } from "../data/redux-state"
import { globalStore } from "./Index"
import { UncertaintyLine, DPoint } from "../utils/extractLines"

const sliderStyle = {
  //color: "black",
  color: "#343a40",
  // '& .MuiSlider-thumb': {
  //     borderRadius: '1px',
  // },
  "& .MuiSlider-markLabel": {
    fontSize: "0.82rem",
    top: "20px",
  },
  marginBottom: "0px",
}

export function XRangeSlider(props: {
  dataXRange: [Date, Date]
  xRange: [Date, Date]
  style?: any
}) {
  const dataXMin = props.dataXRange[0].getFullYear()
  const dataXMax = props.dataXRange[1].getFullYear()
  const xMin = props.xRange[0].getFullYear()
  const xMax = props.xRange[1].getFullYear()
  const value = [xMin, xMax]

  const handleChange = (event: any, newValue: [Date, Date]) => {
    const newRange: [Date, Date] = [
      new Date("" + newValue[0]),
      new Date("" + newValue[1]),
    ]
    globalStore.dispatch(actions.setXRange(newRange))
  }

  const marks = [
    {
      value: dataXMin,
      label: dataXMin,
    },
    {
      value: xMin,
      label: xMin,
    },
    {
      value: xMax,
      label: xMax,
    },
    {
      value: dataXMax,
      label: dataXMax,
    },
  ]
  return (
    <Slider
      marks={marks}
      size="small"
      min={dataXMin}
      max={dataXMax}
      value={value ?? [0, 0]}
      step={1}
      onChange={handleChange as any}
      valueLabelDisplay="off"
      disableSwap={true}
      sx={sliderStyle}
    />
  )
}

export function YRangeSlider(props: {
  dataYRange: [number, number]
  yRange: [number, number]
  style?: any
}) {
  const dataYMin = Math.floor(props.dataYRange[0])
  const dataYMax = Math.ceil(props.dataYRange[1])
  const yMin = Math.floor(props.yRange[0])
  const yMax = Math.ceil(props.yRange[1])
  const value = [yMin, yMax]

  const handleChange = (event: any, newValue: [number, number]) => {
    const newRange: [number, number] = [newValue[0], newValue[1]]
    globalStore.dispatch(actions.setYRange(newRange))
  }

  const marks = [
    {
      value: dataYMin,
      label: dataYMin,
    },
    {
      value: yMin,
      label: yMin,
    },
    {
      value: yMax,
      label: yMax,
    },
    {
      value: dataYMax,
      label: dataYMax,
    },
  ]
  return (
    <Slider
      marks={marks}
      size="small"
      min={dataYMin}
      max={dataYMax}
      value={value}
      onChange={handleChange as any}
      valueLabelDisplay="off"
      disableSwap={true}
      sx={sliderStyle}
    />
  )
}

export function updateChartAfterZoomEvent(
  event: any,
  xScale: d3.ScaleTime<number, number, never>,
  yScale: d3.ScaleLinear<number, number, never>,
  dataXRange: [Date, Date],
  dataYRange: [number, number]
) {
  // recover the new scale
  const newXScale: d3.ScaleTime<number, number, never> =
    event.transform.rescaleX(xScale)
  const newYScale: d3.ScaleLinear<number, number, never> =
    event.transform.rescaleY(yScale)

  // determine new xRange and YRange after zooming
  const newXRange: [Date, Date] = [newXScale.domain()[0], newXScale.domain()[1]]
  const newYRange: [number, number] = [
    newYScale.domain()[0],
    newYScale.domain()[1],
  ]

  // enforce that data-limits are not exceeded
  if (newXRange[0].getTime() < dataXRange[0].getTime()) {
    newXRange[0] = dataXRange[0]
  }
  if (newXRange[1].getTime() > dataXRange[1].getTime()) {
    newXRange[1] = dataXRange[1]
  }
  if (newYRange[0] < dataYRange[0]) {
    newYRange[0] = dataYRange[0]
  }
  if (newYRange[1] > dataYRange[1]) {
    newYRange[1] = dataYRange[1]
  }

  // apply the new xRange and YRange
  globalStore.dispatch(actions.setXRange(newXRange))
  globalStore.dispatch(actions.setYRange(newYRange))
}

function getTimeRangeFromURL(): {
  tMin: number | null
  tMax: number | null
} {
  if (typeof window === "undefined") {
    return { tMin: null, tMax: null }
  }
  const urlParams = new URLSearchParams(window.location.search)
  const tMinRaw = urlParams.get("tMin") || urlParams.get("tmin")
  const tMaxRaw = urlParams.get("tMax") || urlParams.get("tmax")
  const tMin =
    tMinRaw === null || tMinRaw === "" ? null : parseFloat(tMinRaw)
  const tMax =
    tMaxRaw === null || tMaxRaw === "" ? null : parseFloat(tMaxRaw)
  return {
    tMin: Number.isFinite(tMin) ? tMin : null,
    tMax: Number.isFinite(tMax) ? tMax : null,
  }
}

function fractionalYearToDate(fractionalYear: number): Date {
  const year = Math.floor(fractionalYear)
  const startOfYear = new Date(year, 0, 1).getTime()
  const startOfNextYear = new Date(year + 1, 0, 1).getTime()
  const fraction = fractionalYear - year
  return new Date(startOfYear + fraction * (startOfNextYear - startOfYear))
}

export function getXRange(args: {
  extendToFullDecades: boolean
  aboveChart?: boolean
  data: UncertaintyLine[]
}): [Date, Date] {
  const extendToFullDecades = args.extendToFullDecades
  const aboveChart = args.aboveChart
  const min: number | undefined = d3.min(args.data, (line: UncertaintyLine) => {
    return getSingleXRange({
      line,
      extendToFullDecades,
      aboveChart,
    })[0]?.getTime()
  })
  const max: number | undefined = d3.max(args.data, (line: UncertaintyLine) => {
    return getSingleXRange({
      line,
      extendToFullDecades,
      aboveChart,
    })[1]?.getTime()
  })
  const defaultRange: [Date, Date] = [new Date(min as number), new Date(max as number)]

  if (aboveChart) {
    return defaultRange
  }

  const { tMin, tMax } = getTimeRangeFromURL()
  const xMin = tMin !== null ? fractionalYearToDate(tMin) : defaultRange[0]
  const xMax = tMax !== null ? fractionalYearToDate(tMax) : defaultRange[1]

  if (xMin.getTime() >= xMax.getTime()) {
    return defaultRange
  }
  return [xMin, xMax]
}

function getSingleXRange(args: {
  line: UncertaintyLine
  extendToFullDecades: boolean
  aboveChart?: boolean
}): [Date, Date] {
  let minMax: [Date, Date] = d3.extent(args.line.mainPoints, (d: DPoint) => {
    return d.date
  }) as [Date, Date]

  let minDate = new Date(minMax[0])
  let maxDate = new Date(minMax[1])

  minMax = [minDate, maxDate]

  if (!minMax || !minMax[0] || !minMax[1]) {
    return minMax
  }
  let minYear = minMax[0].getFullYear()
  if (args.extendToFullDecades && minYear % 10) {
    minYear = minYear - (minYear % 10)
  } else if (!args.aboveChart) {
    minYear = minYear - 1
  }
  let maxYear = minMax[1].getFullYear()
  if (args.extendToFullDecades && maxYear % 10) {
    maxYear = maxYear + 10 - (maxYear % 10)
  } else if (!args.aboveChart) {
    maxYear = maxYear + 1
  }

  minMax[0] = new Date("" + minYear)
  minMax[1] = new Date("" + maxYear)
  return minMax
}

function countLegendLines(lines: UncertaintyLine[]): number {
  const mainVarMap: Record<string, boolean> = {}
  let coVarCount: number = 0
  for (const line of lines) {
    const key = line.geoRegionKey + "-" + line.subVarKey
    if (!mainVarMap[key]) {
      mainVarMap[key] = true
    } else {
      coVarCount += 1
    }
  }
  const mainVarCount = Object.keys(mainVarMap).length
  return mainVarCount + Math.min(1, coVarCount)
}

const LEGEND_VERTICAL_SPACE_PER_LINE = 0.068

function getYValueRangeFromURL(): {
  minValue: number | null
  maxValue: number | null
} {
  if (typeof window === "undefined") {
    return { minValue: null, maxValue: null }
  }
  const urlParams = new URLSearchParams(window.location.search)
  const minValueRaw = urlParams.get("yMin") || urlParams.get("ymin")
  const maxValueRaw = urlParams.get("yMax") || urlParams.get("ymax")
  const minValue =
    minValueRaw === null || minValueRaw === "" ? null : parseFloat(minValueRaw)
  const maxValue =
    maxValueRaw === null || maxValueRaw === "" ? null : parseFloat(maxValueRaw)
  return {
    minValue: Number.isFinite(minValue) ? minValue : null,
    maxValue: Number.isFinite(maxValue) ? maxValue : null,
  }
}

export function getYRange(
  data: UncertaintyLine[],
  props: AppState
): [number, number] {
  const min: number = d3.min(data, (line: UncertaintyLine) => {
    return getYSingleRange(line, props)[0]
  })!
  const max: number = d3.max(data, (line: UncertaintyLine) => {
    return getYSingleRange(line, props)[1]
  })!
  const legendLines = countLegendLines(data)
  const legendPlace = (max - min) * LEGEND_VERTICAL_SPACE_PER_LINE * legendLines
  const defaultRange: [number, number] = [min, max + legendPlace]
  const { minValue, maxValue } = getYValueRangeFromURL()

  let yMin = minValue ?? defaultRange[0]
  let yMax = maxValue ?? defaultRange[1]

  if (yMin >= yMax) {
    return defaultRange
  }
  return [yMin, yMax]
}

function getYSingleRange(
  line: UncertaintyLine,
  props: AppState
): [number, number] {
  const dataPoints = line.mainPoints
  let minEq = Math.min(...dataPoints.map((dt: DPoint) => dt.value), 0)
  let minMinus = minEq,
    minP90 = minEq
  if (
    line.coVarOption?.sdevEnabled &&
    line.sDevPoints &&
    line.sDevPoints.length > 0
  )
    minMinus = Math.min(...line.sDevPoints.map((dt: DPoint) => dt.down!.value))
  if (line.coVarOption?.urEnabled && line.urPoints && line.urPoints.length > 0)
    minP90 = Math.min(...line.urPoints.map((dt: DPoint) => dt.down!.value))
  let MinArray = [minEq, minMinus, minP90]
  const min: number = d3.min(MinArray)!

  let maxEq = Math.max(...dataPoints.map((dt: DPoint) => dt.value), 0)
  let maxPlus = maxEq,
    maxP95 = maxEq
  if (
    line.coVarOption?.sdevEnabled &&
    line.sDevPoints &&
    line.sDevPoints.length > 0
  )
    maxPlus = Math.max(...line.sDevPoints.map((dt: DPoint) => dt.up!.value), 0)
  if (line.coVarOption?.urEnabled && line.urPoints && line.urPoints.length > 0)
    maxP95 = Math.max(...line.urPoints.map((dt: DPoint) => dt.up!.value), 0)
  let MaxArray = [maxEq, maxPlus, maxP95]
  const max: number = d3.max(MaxArray)!

  const diff = Math.abs(max - min)
  const yMax = max + diff / 20
  let yMin = min < 0 ? min - diff / 8 : min - diff / 8
  return [yMin, yMax]
}
